import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { ExportFile } from '~/shared/data-contract/common';
import { exportFileSchema } from '~/shared/data-contract/common';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import { useGlobalPermissions } from '../global-permissions';

export const useTalentDirectoryPermissions = (): {
    readonly canAccessTalentDirectory: boolean;
} => {
    const { data: globalPermissions } = useGlobalPermissions();

    return {
        canAccessTalentDirectory: globalPermissions?.canUserTalentDirectory ?? false,
    };
};

const useDirectoryExport = (pathname: Pathname): UseMutationResult<ExportFile> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.post(exportFileSchema, {
                pathname,
                requestOptions: {
                    timeout: 90_000,
                },
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        mutationKey: [pathname, 'POST'],
    });
};

export const useTalentDirectorySkillsExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/skills/export');

export const useTalentDirectoryEmployeeExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/users/export');

export const useTalentDirectoryJobExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/jobs/export');

export const useTalentDirectoryLearningOpportunityExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/learning-opportunities/export');
